import React, { useEffect } from 'react'
import { useOverflowObserver } from '../_hooks/OverflowObserver'
import { parseFilename } from '../_utils/filename'
import type { Props } from '../_utils/types'
import {
  StyledCaptionText,
  StyledMultilineCaption,
  StyledPlaceholderCaption,
  StyledSingleLineCaption,
  StyledSinglelineCaptionBody,
  StyledSinglelineCaptionEnding,
} from './Thumbnail.styles'
import type {
  CaptionProps,
  MultilineCaptionProps,
  SinglelineCaptionProps,
} from './Thumbnail.types'

function MultilineCaption({
  disabled,
  caption,
  error,
  layout,
  qa,
  onCaptionOverflowChange,
  ...props
}: MultilineCaptionProps & Props) {
  const { ref, isOverflowing } = useOverflowObserver()

  useEffect(() => {
    onCaptionOverflowChange?.(isOverflowing)
  }, [isOverflowing, onCaptionOverflowChange])

  return (
    <span data-qa={qa}>
      <StyledMultilineCaption
        ref={ref}
        $disabled={disabled}
        $error={error}
        $layout={layout}
        {...props}
      >
        <StyledCaptionText>{caption}</StyledCaptionText>
      </StyledMultilineCaption>
    </span>
  )
}

function SinglelineCaption({
  caption,
  error,
  disabled,
  layout,
  qa,
  onCaptionOverflowChange,
  ...props
}: SinglelineCaptionProps & Props) {
  const { ref, isOverflowing } = useOverflowObserver()
  const { filename, extension, isFilename } = parseFilename(caption)

  useEffect(() => {
    onCaptionOverflowChange?.(isOverflowing)
  }, [isOverflowing, onCaptionOverflowChange])

  return (
    <StyledSingleLineCaption
      $error={error}
      $disabled={disabled}
      $layout={layout}
      data-qa={qa}
      {...props}
    >
      <StyledSinglelineCaptionBody ref={ref}>
        {isFilename ? `${filename}.` : caption}
      </StyledSinglelineCaptionBody>
      {isFilename && (
        <StyledSinglelineCaptionEnding>
          {extension}
        </StyledSinglelineCaptionEnding>
      )}
    </StyledSingleLineCaption>
  )
}

export function ThumbnailCaption({
  layout,
  disabled = false,
  error = false,
  caption,
  hasCaptionPlaceholder,
  multiline,
  qa = '',
  ...props
}: CaptionProps) {
  if (!caption) {
    const captionPlaceholder = hasCaptionPlaceholder && '- -'

    if (captionPlaceholder) {
      return (
        <StyledPlaceholderCaption
          $disabled={disabled}
          $error={error}
          $layout={layout}
          data-qa={qa}
        >
          <StyledCaptionText>{captionPlaceholder}</StyledCaptionText>
        </StyledPlaceholderCaption>
      )
    }

    return null
  }

  if (multiline) {
    return (
      <MultilineCaption
        disabled={disabled}
        caption={caption}
        error={error}
        layout={layout}
        qa={qa}
        onCaptionOverflowChange={props.onCaptionOverflowChange}
        {...props}
      />
    )
  }

  return (
    <SinglelineCaption
      caption={caption}
      disabled={disabled}
      error={error}
      layout={layout}
      qa={qa}
      onCaptionOverflowChange={props.onCaptionOverflowChange}
      {...props}
    />
  )
}
